import React, { Component } from "react";
import { observer } from "mobx-react";

import { defined } from "@formant/core/common/defined";
import { IView } from "@formant/core/model/IView";

import { routerStore, viewStore } from "../../../stores";
import { isViewLiveConfigurable } from "../../../utils/isViewLiveConfigurable";
import { localSettingObservable } from "../../../utils/localSettingObservable";
import {
    DataTable,
    defaultSimpleSortState,
    ISimpleSortState,
} from "../../common/DataTable";
import { IDataTableColumn } from "../../common/DataTable/IDataTableColumn";
import { Help } from "../../common/Help";
import { RestrictedComponent } from "../../common/RestrictedComponent";
import { Scrollable } from "../../common/Scrollable";
import {
    applySimpleFilter,
    emptySimpleFilterState,
    ISimpleFilterState,
    SimpleFilter,
    tagDimensions,
} from "../../common/SimpleFilter";
import { StandardButton } from "../../common/StandardButton";
import { StatsHeader } from "../../common/StatsHeader";
import { Tags } from "../../common/Tags";
import { OverviewViewName } from "../../Overview/OverviewV2/OverviewV2";
import { RowDate } from "../RowDate";
import { RowTitle } from "../RowTitle";
import { SettingsPage } from "../SettingsPage";
import { Actions } from "./Actions";

@observer
export class Views extends Component {
    @localSettingObservable("settings-views-filter")
    private filter: ISimpleFilterState = emptySimpleFilterState;

    @localSettingObservable("settings-views-sort")
    private sort: ISimpleSortState = defaultSimpleSortState;

    public render() {
        const { routeTo } = routerStore;
        const columns: IDataTableColumn<IView>[] = [
            {
                header: "Name",
                row: _ => (
                    <RowTitle
                        title={_.name}
                        icon={_.url ? "utility" : "view"}
                    />
                ),
            },
            {
                header: "Tags",
                row: _ => <Tags tags={_.tags} />,
            },
            {
                header: "Created",
                row: _ => <RowDate date={_.createdAt} />,
                hideBelow: "md",
            },
            {
                header: "Updated",
                row: _ => <RowDate date={_.updatedAt} />,
                hideBelow: "md",
            },
            {
                header: "",
                row: (_, i) => (
                    <RestrictedComponent
                        resource="views"
                        requiredAccess="administrator"
                    >
                        <Actions view={_} views={views || []} index={i} />
                    </RestrictedComponent>
                ),
            },
        ];

        const views = viewStore.list();

        const { filter, onChangeFilter, sort, onChangeSort } = this;

        const dimensions = [...tagDimensions(views || [], _ => _.tags)];

        const filteredViews = applySimpleFilter(
            (views || []).filter(val => val.name !== OverviewViewName),
            dimensions,
            getViewText,
            filter,
        );

        return (
            <SettingsPage
                title="Views"
                extra={
                    <Help
                        text="A view is a group of visualization modules within the device channel. You can add, edit or delete views, and you can apply views to new or existing devices."
                        to="https://docs.formant.io/docs/custom-web-views"
                    />
                }
                actions={
                    <RestrictedComponent
                        resource="views"
                        requiredAccess="administrator"
                    >
                        <StandardButton
                            to={routeTo.createView()}
                            data-testid="add-view-button"
                        >
                            Add view
                        </StandardButton>
                    </RestrictedComponent>
                }
            >
                <StatsHeader
                    icon="view"
                    loading={!views}
                    totalQuantity={views?.length || 0}
                    totalLabel="Views"
                />
                <SimpleFilter
                    dimensions={dimensions}
                    rows={views || []}
                    filter={filter}
                    onChangeFilter={onChangeFilter}
                />
                <Scrollable>
                    <DataTable
                        rows={filteredViews || []}
                        columns={columns}
                        loading={views === undefined}
                        noResultsText="No view found"
                        onRowClicked={this.onEdit}
                        sort={sort}
                        onChangeSort={onChangeSort}
                    />
                </Scrollable>
            </SettingsPage>
        );
    }

    private onChangeFilter = (filter: ISimpleFilterState) => {
        this.filter = filter;
    };

    private onChangeSort = (sort: ISimpleSortState) => {
        this.sort = sort;
    };

    private onEdit = async (view: IView) => {
        const { navigate, routeTo } = routerStore;
        await navigate(
            routeTo.editView(
                { id: defined(view.id) },
                {
                    tab: isViewLiveConfigurable(view)
                        ? "Edit layout"
                        : "Choose streams",
                },
            ),
        );
    };
}

function getViewText(view: IView): string {
    return view.name;
}
